import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, SettingsCard, Typography } from "@humansignal/ui";
import { useUpdatePageTitle, createTitleFromSegments, instructionsToHtml } from "@humansignal/core";
import { Form, Input, TextArea, Toggle } from "../../components/Form";
import { MenubarContext } from "../../components/Menubar/Menubar";
import { cn } from "../../utils/bem";

import { ModelVersionSelector } from "./AnnotationSettings/ModelVersionSelector";
import { ProjectContext } from "../../providers/ProjectProvider";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { RadioGroup } from "../../components/Form/Elements/RadioGroup/RadioGroup";
import { useTranslation } from "../../i18n/LocaleProvider";
import { modal } from "../../components/Modal/Modal";
import { useAPI } from "../../providers/ApiProvider";
import { useToast } from "@humansignal/ui";

// Radio options for "how many annotation regions per task before submit".
// Mapped to the project fields ``regions_min_per_annotation`` (int, 0 = off)
// and ``regions_max_per_annotation`` (int | null).
// Each option also has a one-line description — the shared
// ``<RadioGroup simple>`` renders Label primitives that look right
// only when there's content below the label, otherwise the pills
// collapse into ~2px-tall stripes. Same pattern as the General
// settings Task-sampling group.
const REGIONS_MODES = [
  {
    key: "none",
    label: "No minimum",
    description: "Default. Annotators can submit a task without creating any regions.",
    min: 0,
    max: null,
  },
  {
    key: "one",
    label: "Exactly one",
    description:
      "Single-answer tasks (single-class QA, single-best label). Submit blocked until exactly one region exists.",
    min: 1,
    max: 1,
  },
  {
    key: "many",
    label: "One or more",
    description: "ABSA / multi-aspect / multi-region tasks. Submit blocked until at least one region exists.",
    min: 1,
    max: null,
  },
];

// Options for ``Project.skip_queue`` (projects/models.py::SkipQueue).
// Rendered as a native select with a one-line dynamic description that
// swaps with the value — same idiom as the Visibility selects and the
// raw <select name="annotator_language"> on General settings.
const SKIP_QUEUE_OPTIONS = [
  {
    value: "REQUEUE_FOR_OTHERS",
    label: "Requeue for others",
    description: "A skipped task goes back to the queue for a different annotator.",
  },
  {
    value: "REQUEUE_FOR_ME",
    label: "Requeue for me",
    description: "Skipped tasks return to the same annotator later.",
  },
  {
    value: "IGNORE_SKIPPED",
    label: "Ignore skipped",
    description: "A skip counts as done, the task never comes back.",
  },
];

// Non-annotator roles the owner can permit to annotate (annotators are
// never listed — they can always annotate). Codes match core/roles.py.
const ANNOTATION_ROLE_OPTIONS = [
  { code: "RE", label: "Reviewers" },
  { code: "MA", label: "Managers" },
  { code: "AD", label: "Admins" },
  { code: "OW", label: "Owners" },
];

const detectRegionsMode = (project) => {
  const min = project?.regions_min_per_annotation ?? 0;
  const max = project?.regions_max_per_annotation ?? null;
  for (const m of REGIONS_MODES) {
    if (m.min === min && (m.max ?? null) === (max ?? null)) return m.key;
  }
  return "custom";
};

/** ``variant="collect"``: the cards a collection project shares with
 *  annotation (instructions, skip, empty submissions, who can contribute),
 *  worded for contributors, without pre-labeling, regions or bulk mode.
 *  ``embedded`` drops the page header (the Collection page has its own). */
export const AnnotationSettings = ({ variant = "annotate", embedded = false } = {}) => {
  const collect = variant === "collect";
  const { t } = useTranslation();
  const { project, fetchProject } = useContext(ProjectContext);
  const api = useAPI();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [uploadingName, setUploadingName] = useState("");
  const pageContext = useContext(MenubarContext);
  const formRef = useRef();
  const [collab, setCollab] = useState(null);
  const [regionsMode, setRegionsMode] = useState(() => detectRegionsMode(project));
  useEffect(() => {
    setRegionsMode(detectRegionsMode(project));
  }, [project?.id]);

  // Controlled so the one-line description under the select swaps with
  // the value; the ``name`` attribute still hands the value to the Form
  // on submit (same as the hidden regions inputs below).
  const [skipQueue, setSkipQueue] = useState(project?.skip_queue ?? "REQUEUE_FOR_OTHERS");
  useEffect(() => {
    setSkipQueue(project?.skip_queue ?? "REQUEUE_FOR_OTHERS");
  }, [project?.id, project?.skip_queue]);
  // Empty-submission gate: the editor + server enforcement always
  // existed (annotations:deny-empty); this is the missing owner knob.
  const [emptyOk, setEmptyOk] = useState(project?.enable_empty_annotation !== false);
  useEffect(() => {
    setEmptyOk(project?.enable_empty_annotation !== false);
  }, [project?.id, project?.enable_empty_annotation]);

  // Which non-annotator roles may create annotations. Annotators always
  // can; the project creator and platform staff are never locked out.
  // Submitted as a JSON string via the hidden input below — the backend
  // ``validate_annotation_roles`` parses/whitelists it.
  const [annRoles, setAnnRoles] = useState(() =>
    Array.isArray(project?.annotation_roles) ? project.annotation_roles : ["OW", "AD", "MA", "RE"],
  );
  useEffect(() => {
    setAnnRoles(Array.isArray(project?.annotation_roles) ? project.annotation_roles : ["OW", "AD", "MA", "RE"]);
  }, [project?.id]);
  const toggleAnnRole = (code) =>
    setAnnRoles((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  useUpdatePageTitle(createTitleFromSegments([project?.title, embedded ? "Collection" : "Annotation Settings"]));

  useEffect(() => {
    pageContext.setProps({ formRef });
  }, [formRef]);

  const updateProject = useCallback(() => {
    fetchProject(project.id, true);
  }, [project]);

  // Show the instructions exactly as annotators will see them (Markdown or
  // HTML, rendered and sanitised by the same helper the editor uses),
  // reading the unsaved text straight from the field so no save is needed.
  const previewInstructions = useCallback(() => {
    const text = document.getElementById("expert_instruction")?.value ?? "";
    const html = instructionsToHtml(text);
    modal({
      title: t("pages.settings.annotation_settings.labeling_instructions"),
      style: { width: 840, maxWidth: "95vw" },
      body: text.trim() ? (
        <div className={cn("instructions-body").toClassName()} dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <p style={{ margin: 0, color: "var(--color-neutral-content-subtler)" }}>
          {t("pages.settings.annotation_settings.preview_empty")}
        </p>
      ),
    });
  }, [t]);

  // "Add file": upload one image / PDF / media file to the project and insert
  // the matching Markdown or HTML snippet at the cursor. The textarea value
  // is set through the native setter so the form sees it as a real edit.
  const insertAtCursor = useCallback((snippet) => {
    const ta = document.getElementById("expert_instruction");
    if (!ta) return;
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? start;
    const before = ta.value.slice(0, start);
    const after = ta.value.slice(end);
    const pad = before && !before.endsWith("\n") ? "\n\n" : "";
    const next = `${before}${pad}${snippet}\n${after}`;
    const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value")?.set;
    if (setter) setter.call(ta, next);
    else ta.value = next;
    ta.dispatchEvent(new Event("input", { bubbles: true }));
    const caret = before.length + pad.length + snippet.length + 1;
    ta.focus();
    ta.setSelectionRange(caret, caret);
  }, []);

  const addFile = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file || !project?.id) return;
      setUploadingName(file.name);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.callApi("uploadInstructionFile", {
          params: { pk: project.id },
          headers: { "Content-Type": "multipart/form-data" },
          body: fd,
          errorFilter: () => true,
        });
        if (!res || res.error || !res.url) {
          const message = res?.validation_errors?.non_field_errors?.[0] ?? res?.error ?? res?.detail;
          throw new Error(message || t("pages.settings.annotation_settings.add_file_failed"));
        }
        const title = file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ");
        const snippet =
          res.kind === "image"
            ? `![${title}](${res.url})`
            : res.kind === "pdf"
              ? `<iframe src="${res.url}" width="100%" height="520" title="${title}"></iframe>`
              : res.kind === "video"
                ? `<video src="${res.url}" controls width="100%"></video>`
                : `<audio src="${res.url}" controls></audio>`;
        insertAtCursor(snippet);
      } catch (e) {
        toast.show({ message: e?.message || t("pages.settings.annotation_settings.add_file_failed"), type: "error" });
      } finally {
        setUploadingName("");
      }
    },
    [api, project?.id, insertAtCursor, t, toast],
  );

  return (
    <div className={cn("settings-page").toClassName()}>
      {embedded ? null : (
        <SettingsPageHeader
          title={t("pages.settings.annotation_settings.annotation")}
          subtitle={t("pages.settings.annotation_settings.instructions_prelabeling_and_pertask_ann")}
        />
      )}
      <Form
        ref={formRef}
        action="updateProject"
        formData={{ ...project }}
        params={{ pk: project.id }}
        onSubmit={updateProject}
      >
        <div className={cn("settings-page").elem("body").toClassName()}>
          <SettingsCard
            title={
              collect ? "Instructions for contributors" : t("pages.settings.annotation_settings.labeling_instructions")
            }
            description={
              collect
                ? "What to collect and how, shown to contributors before they start."
                : t("pages.settings.annotation_settings.guidance_shown_to_annotators_before_they")
            }
          >
            <Form.Row columnCount={1}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <Toggle label={t("pages.settings.annotation_settings.show_before_labeling")} name="show_instruction" />
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,video/mp4,video/webm,audio/mpeg,audio/wav,audio/ogg,audio/mp4"
                    hidden
                    onChange={addFile}
                    data-testid="instruction-file-input"
                  />
                  <Button
                    type="button"
                    variant="primary"
                    look="outlined"
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!!uploadingName}
                    waiting={!!uploadingName}
                    aria-label={t("pages.settings.annotation_settings.add_file_hint")}
                    title={t("pages.settings.annotation_settings.add_file_hint")}
                  >
                    {uploadingName
                      ? t("pages.settings.annotation_settings.add_file_uploading", { name: uploadingName })
                      : t("pages.settings.annotation_settings.add_file")}
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    look="outlined"
                    size="small"
                    onClick={previewInstructions}
                    aria-label={t("pages.settings.annotation_settings.preview_instructions")}
                  >
                    {t("pages.settings.annotation_settings.preview")}
                  </Button>
                </div>
              </div>
              <TextArea id="expert_instruction" name="expert_instruction" style={{ minHeight: 128 }} />
            </Form.Row>
          </SettingsCard>

          {!collect && (
            <SettingsCard
              title={t("pages.settings.annotation_settings.prelabeling")}
              description={t("pages.settings.annotation_settings.seed_new_tasks_with_predictions_so_annot")}
            >
              <Form.Row columnCount={1}>
                <div>
                  <Toggle
                    label={t("pages.settings.annotation_settings.use_predictions_to_prelabel_tasks")}
                    description={
                      <span>{t("pages.settings.annotation_settings.enable_and_select_which_set_of_predictio")}</span>
                    }
                    name="show_collab_predictions"
                    onChange={(e) => {
                      setCollab(e.target.checked);
                    }}
                  />
                </div>

                {(collab !== null ? collab : project.show_collab_predictions) && <ModelVersionSelector />}
              </Form.Row>
            </SettingsCard>
          )}

          {!collect && (
            <SettingsCard
              title={t("pages.settings.annotation_settings.annotations_per_task")}
              description={t("pages.settings.annotation_settings.enforce_how_many_annotation_regions_an_a")}
            >
              {/* No Form.Row wrapper — Form.Row enforces a single-column
                  grid that squashed the radio pills together. The Task-
                  sampling group on General settings renders the
                  ``<RadioGroup>`` directly inside the SettingsCard; doing
                  the same here keeps spacing consistent across pages.
                  ``simple`` mode is what makes each Button render its
                  ``label`` (without it the buttons were blank). */}
              <RadioGroup
                label={null}
                labelProps={{ size: "large" }}
                name="__regions_mode_ui"
                simple
                value={regionsMode}
                onChange={setRegionsMode}
              >
                {REGIONS_MODES.map((m) => (
                  <RadioGroup.Button key={m.key} value={m.key} label={m.label} description={m.description} />
                ))}
              </RadioGroup>
              {/* Hidden inputs the Form picks up on submit — mapped from the
                  selected radio option. ``regions_max_per_annotation`` is an
                  empty string for "unlimited" (backend treats blank as null).
                  NOT wrapped in Form.Row: an all-hidden row still renders
                  the grid's gap/margins and left a big dead band under the
                  radio group. */}
              <input
                type="hidden"
                name="regions_min_per_annotation"
                value={REGIONS_MODES.find((m) => m.key === regionsMode)?.min ?? 0}
              />
              <input
                type="hidden"
                name="regions_max_per_annotation"
                value={REGIONS_MODES.find((m) => m.key === regionsMode)?.max ?? ""}
              />
            </SettingsCard>
          )}

          <SettingsCard
            title="Skip behaviour"
            description={
              collect
                ? "What happens to a prompt when a contributor skips it."
                : "What happens to a task when an annotator skips it."
            }
          >
            <Form.Row columnCount={1}>
              <div className="flex flex-col gap-2">
                <label htmlFor="skip_queue" className="font-medium">
                  {collect ? "When a prompt is skipped" : "When a task is skipped"}
                </label>
                <select
                  id="skip_queue"
                  name="skip_queue"
                  className="border bg-neutral-surface"
                  value={skipQueue}
                  onChange={(e) => setSkipQueue(e.target.value)}
                  style={{
                    maxWidth: 420,
                    height: 40,
                    minHeight: 40,
                    boxSizing: "border-box",
                    padding: "0 16px",
                    borderRadius: 5,
                    fontSize: "var(--font-size-field-input)",
                    lineHeight: "var(--line-height-snug)",
                  }}
                >
                  {SKIP_QUEUE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <Typography size="small" className="text-neutral-content-subtler">
                  {SKIP_QUEUE_OPTIONS.find((o) => o.value === skipQueue)?.description}
                </Typography>
              </div>
            </Form.Row>
          </SettingsCard>

          <SettingsCard
            title="Submission requirements"
            description={
              collect
                ? "Whether a contributor can submit a record without filling anything in."
                : "Whether an annotator can submit a task without labelling anything."
            }
          >
            <Form.Row columnCount={1}>
              <div className="flex flex-col gap-2">
                <label htmlFor="enable_empty_annotation" className="font-medium">
                  Empty submissions
                </label>
                <select
                  id="enable_empty_annotation"
                  name="enable_empty_annotation"
                  className="border bg-neutral-surface"
                  value={String(emptyOk)}
                  onChange={(e) => setEmptyOk(e.target.value === "true")}
                  style={{
                    maxWidth: 420,
                    height: 40,
                    minHeight: 40,
                    boxSizing: "border-box",
                    padding: "0 16px",
                    borderRadius: 5,
                    fontSize: "var(--font-size-field-input)",
                    lineHeight: "var(--line-height-snug)",
                  }}
                >
                  <option value="true">Allowed</option>
                  <option value="false">Require at least one region or answer</option>
                </select>
                <Typography size="small" className="text-neutral-content-subtler">
                  {emptyOk
                    ? "Submit works even when nothing was labelled. Use Skip for tasks with genuinely nothing to label."
                    : "Submit stays disabled until the annotator has labelled at least one region or answered at least one field. Skip remains available."}
                </Typography>
              </div>
            </Form.Row>
          </SettingsCard>

          <SettingsCard
            title={collect ? "Who can contribute" : "Who can annotate"}
            description={
              collect
                ? "Annotators can always contribute. Pick which other roles may also submit records."
                : "Annotators can always annotate. Pick which other roles may also create annotations."
            }
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {ANNOTATION_ROLE_OPTIONS.map((o) => (
                <label key={o.code} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={annRoles.includes(o.code)} onChange={() => toggleAnnRole(o.code)} />
                  <span>{o.label}</span>
                </label>
              ))}
              <Typography size="small" className="text-neutral-content-subtler">
                {collect
                  ? "The project creator and platform staff can always contribute."
                  : "The project creator and platform staff can always annotate."}
              </Typography>
            </div>
            {/* Hidden input the Form submits — a JSON string the backend
                  validate_annotation_roles parses and whitelists. */}
            <input type="hidden" name="annotation_roles" value={JSON.stringify(annRoles)} />
          </SettingsCard>

          {!collect && (
            <SettingsCard
              title={t("pages.settings.annotation_settings.bulk_mode", undefined, "Bulk mode")}
              description={t(
                "pages.settings.annotation_settings.bulk_mode_description",
                undefined,
                "Rapid-fire view for simple classification, annotators label a whole page of tasks with one click each.",
              )}
            >
              {/* Compact single row: toggle carries the explanation,
                  the page-size input sits beside it — the old stacked
                  Form.Row left a deep dead band under the toggle
                  (owner-requested tightening). */}
              <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 320px", minWidth: 0 }}>
                  <Toggle
                    name="bulk_mode_enabled"
                    disabled={!project.config_suitable_for_bulk_annotation}
                    label={t("pages.settings.annotation_settings.enable_bulk_mode", undefined, "Enable bulk mode")}
                    description={
                      project.config_suitable_for_bulk_annotation
                        ? t(
                            "pages.settings.annotation_settings.bulk_mode_available",
                            undefined,
                            "Adds a Bulk label view for annotators on this project.",
                          )
                        : t(
                            "pages.settings.annotation_settings.bulk_mode_unavailable",
                            undefined,
                            "Unavailable for this labeling config, bulk mode needs a single Choices control (no regions or free-text inputs).",
                          )
                    }
                  />
                </div>
                <div style={{ flex: "0 0 180px" }}>
                  <Input
                    name="bulk_page_size"
                    type="number"
                    min={1}
                    max={100}
                    disabled={!project.config_suitable_for_bulk_annotation}
                    label={t("pages.settings.annotation_settings.bulk_page_size", undefined, "Bulk page size")}
                    description={t(
                      "pages.settings.annotation_settings.bulk_page_size_description",
                      undefined,
                      "Tasks per page (1–100).",
                    )}
                  />
                </div>
              </div>
            </SettingsCard>
          )}

          <Form.Actions>
            <Form.Indicator>
              <span case="success">Saved!</span>
            </Form.Indicator>
            <Button
              type="submit"
              variant="primary"
              className="w-[120px]"
              aria-label={t("pages.settings.annotation_settings.save_annotation_settings")}
            >
              Save
            </Button>
          </Form.Actions>
        </div>
      </Form>
    </div>
  );
};

AnnotationSettings.title = "Annotation";
AnnotationSettings.path = "/annotation";
